/**
 * Топ 3 — 1-р байр ТӨВД, дээш өргөгдөж гэрэлтэнэ (dark glass, 2026-10-05).
 *
 * ⚠️ Энэ нь БАГИЙН (эсвэл цуглуулагчийн) байр. Салбарын доторх
 *    ажилтныг энд хэзээ ч харуулахгүй.
 *
 * ⚠️ Том тоо нь ОНОО, дүн биш. Эрэмбэ нормчилсон оноогоор гардаг тул
 *    дизайны «сая₮»-ийг том бичвэл бага борлуулалттай салбар дээгүүр
 *    зогсож байгаа нь ойлгомжгүй болно. Дүн нь доорх мөрөнд.
 *    Доод зурвас = тэргүүлэгчийн оноотой харьцуулсан хувь.
 */

import { formatQty } from '@boots/domain';
import type { LeaderboardEntry } from '../../lib/types';
import { useTween } from '../../lib/use-tween';
import { CountUp } from '../count-up';
import { Meter } from '../meter';
import { headlineOf } from './leaderboard-card';

/** Дэлгэцэн дээрх дараалал: 2 · 1 · 3 */
const ORDER = [1, 0, 2];

const MEDAL: Record<number, string> = {
  1: 'bg-medal-gold',
  2: 'bg-medal-silver',
  3: 'bg-medal-bronze',
};

export function Podium({
  entries, isBranch, myStoreId,
}: {
  entries: LeaderboardEntry[];
  isBranch: boolean;
  myStoreId: string | null;
}) {
  const top = entries.slice(0, 3);
  if (top.length === 0) return null;
  const lead = Number(top[0]?.totalScore ?? 0);

  return (
    <ul className="grid grid-cols-1 items-end gap-3.5 sm:grid-cols-3 sm:pt-2.5">
      {ORDER.filter((i) => i < top.length).map((i) => {
        const e = top[i];
        if (!e) return null;
        return (
          <Tile
            key={e.subject.id}
            entry={e}
            isBranch={isBranch}
            mine={isBranch && e.subject.id === myStoreId}
            share={lead > 0 ? (Number(e.totalScore) / lead) * 100 : 0}
          />
        );
      })}
    </ul>
  );
}

function Tile({ entry: e, isBranch, mine, share }: {
  entry: LeaderboardEntry;
  isBranch: boolean;
  mine: boolean;
  share: number;
}) {
  const first = e.rank === 1;
  const bar = useTween(share);
  const target = e.breakdown.find((b) => b.metric === 'targetAchievement');

  return (
    <li
      className={`relative flex flex-col gap-1.5 overflow-hidden rounded-well border px-5 py-card sm:min-h-47.5 ${
        first
          ? 'border-accent-300 bg-linear-160 from-accent-200 to-transparent ' +
            'shadow-[0_0_40px_color-mix(in_oklab,var(--color-accent)_30%,transparent)] sm:-translate-y-2.5'
          : 'border-hair bg-well'
      } ${mine ? 'ring-2 ring-accent/40' : ''}`}
    >
      <div className="flex items-center gap-3">
        <span
          className={`num grid size-7.5 shrink-0 place-items-center rounded-pill text-meta font-extrabold
            text-medal-ink shadow-[inset_0_-6px_10px_rgb(0_0_0/22%)] ${MEDAL[e.rank] ?? 'bg-track-2'} ${
            first ? 'pop-in' : ''
          }`}
        >
          {e.rank}
        </span>
        {/* Кирилл нэр урт — тайрахгүй, боож өгнө */}
        <span className={`min-w-0 text-h3 text-pretty ${first ? 'font-extrabold' : 'font-semibold'}`}>
          {e.subject.name}
        </span>
      </div>

      <span className="mt-3 flex items-baseline gap-2 font-display text-num-md font-semibold">
        <CountUp text={formatQty(Number(e.totalScore))} />
        <span className="font-sans text-h3 font-semibold tracking-normal text-ink-3">оноо</span>
      </span>

      <div className="flex flex-wrap items-baseline gap-x-2 text-label text-ink-2">
        <span className="num">{headlineOf(e, isBranch)}</span>
        {isBranch && e.meta?.staffCount !== undefined ? (
          <span>· {formatQty(e.meta.staffCount)} ажилтан</span>
        ) : null}
        {e.meta?.isNew ? <span className="text-accent-700">· шинэ</span> : null}
        {target ? (
          <span className={Math.round(target.raw) >= 100 ? 'text-accent-700' : 'text-warn-ink'}>
            · зорилт {Math.round(target.raw)}%
          </span>
        ) : e.excluded.includes('targetAchievement') ? (
          <span className="text-ink-3">· зорилтгүй</span>
        ) : null}
      </div>

      <Meter
        value={bar}
        tone={first ? 'accent' : 'neutral'}
        size="md"
        className="mt-auto pt-0"
        label={`Тэргүүлэгчийн онооны ${Math.round(share)}%`}
      />
    </li>
  );
}
